// External Libraries
import { Trans, t } from "@lingui/macro";
import { NextSeo } from "next-seo";
import { useRouter } from "next/compat/router";
import { GetStaticPaths, GetStaticProps, NextPage } from "next/types";
import { ParsedUrlQuery } from "querystring";
import React, { useEffect, useMemo, useState } from "react";
import { useIsSSR } from "react-aria";
import styled from "styled-components";
import {
  fetchDestinationPage,
  useDestinationPage,
} from "~/applications/OfferSearch/Api/useDestinationPage";
import {
  fetchDestinationPages,
  useDestinationPages,
} from "~/applications/OfferSearch/Api/useDestinationPages";
import { fetchHotelPagesOfDestination } from "~/applications/OfferSearch/Api/useHotelPagesOfDestination";
import { useOffers } from "~/applications/OfferSearch/Api/useOffers";
import useOffersSorter from "~/applications/OfferSearch/Api/useOffersSorter";
import { CancellationPolicyId } from "~/applications/OfferSearch/Domain/CancellationPolicyId";
import {
  DestinationMenuEntry,
  DestinationPage,
  destinationToMenuEntry,
} from "~/applications/OfferSearch/Domain/DestinationPage";
import ErrorBoundary from "~/applications/OfferSearch/Ui/ErrorBoundary";
import OfferSearchLayout, {
  AccommodationsSection,
} from "~/applications/OfferSearch/Ui/OfferSearchLayout";
import TextualStayRequestForm from "~/applications/OfferSearch/Ui/StayRequestForm/TextualStayRequestForm";
import { useSearchFromQuery } from "~/applications/OfferSearch/Ui/StayRequestForm/useSearch";
import { BrandIdentity } from "~/components/BrandIdentity/BrandIdentity";
import { fetchBrandIdentity } from "~/components/BrandIdentity/useBrandIdentity";
import Footer from "~/components/Footer";
import StayRequest from "~/core/StayRequest";
import { AVAILABLE_LOCALES, DEFAULT_LOCALE } from "~/core/locale";
import {
  gogailleJourneyRoute,
  homepageRoute,
  hotelPageRoute,
  offersRoute,
} from "~/core/routes";
import { useRouterQueryParameter } from "~/core/useRouterQueryParameter";
import useStayRequest from "~/core/useStayRequest";
import { Button } from "~/guidelines/Button";
import Header from "~/guidelines/Header";
import { LottieOfferLoader } from "~/guidelines/Loaders";
import mediaQueries from "~/guidelines/Theme/mediaQueries";
import { body1, heading1, heading5 } from "~/guidelines/Typography";
import DestinationsCarousel from "~/pages/new-home/_components/DestinationsCarousel";
import Faq from "~/pages/new-home/_components/Faq";
import { serverSideTranslations } from "~/translations/serverSideTranslations";
import { ServerSideGeneratedI18nNamespace } from "~/translations/types";
import HotelCard from "./_components/HotelCard";

const PUBLIC_URL = process.env.PUBLIC_URL;

const ErrorLayout = styled.div`
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(5)};
  padding: ${({ theme }) => theme.spacing(4, 5)};

  ${mediaQueries.sm()} {
    padding: ${({ theme }) => theme.spacing(8, 5)};
  }
`;

const ErrorExplanation = styled.p`
  margin: 0;
  padding: 0;
  ${body1};
`;

const CarouselWrapper = styled.div`
  display: block;
  max-width: 100vw;
`;

const FaqContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 50px auto;
`;

const HeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Title = styled.h3`
  padding: 50px 0 0 0;
  margin: 0;
  ${heading5};

  ${mediaQueries.xl()} {
    padding: 120px 0 0 0;
  }
`;

const Subtitle = styled.h1`
  ${heading1};
  padding: 0;
  margin: 0;
`;

const SoldOutDestinationError = () => (
  <ErrorLayout>
    <ErrorExplanation>
      <Trans>
        We are a victim of our own success, and there are no rooms available at
        this date.
        <br />
        You can try a new date.
      </Trans>
    </ErrorExplanation>
    <Button kind="tertiary" href={homepageRoute()}>
      <Trans>Do a new search</Trans>
    </Button>
  </ErrorLayout>
);

const NoOffersAvailableError = () => (
  <ErrorLayout>
    <ErrorExplanation>
      <Trans>
        No more availabilities for this hotel, we&apos;re victims of our own
        success.
      </Trans>
    </ErrorExplanation>
    <Button kind="tertiary" href={homepageRoute()}>
      <Trans>Do a new search</Trans>
    </Button>
  </ErrorLayout>
);

const PastStayError = () => (
  <ErrorLayout>
    <ErrorExplanation>
      <Trans>
        To reserve these dates, you must own a Delorean...
        <br />
        However, we can meet in the future if you feel like it.
      </Trans>
    </ErrorExplanation>
    <Button kind="tertiary" href={homepageRoute()}>
      <Trans>Do a new search</Trans>
    </Button>
  </ErrorLayout>
);

const OutOfBoundsGuestsError = () => (
  <ErrorLayout>
    <ErrorExplanation>
      <Trans>
        You cannot book for such a big number of guests.
        <br />
        If you want to make one of our Loge yours, feel free to contact us.
        Otherwise...
      </Trans>
    </ErrorExplanation>
    <Button kind="tertiary" href={homepageRoute()}>
      <Trans>Do a new search</Trans>
    </Button>
  </ErrorLayout>
);

type ClientSideOnlyOffersPageProps = {
  locale: AVAILABLE_LOCALES;
  destinationPage: DestinationPage;
  destinations: Array<DestinationPage>;
  selectedDestination: DestinationMenuEntry;
  selectedStayRequest: StayRequest;
  selectedCancellationPolicyId: CancellationPolicyId;
  selectedLogesFilter: Array<string>;
};

interface OffersPageParams extends ParsedUrlQuery {
  destinationSlug: string;
}

export interface OffersPageProps extends ServerSideGeneratedI18nNamespace {
  locale: AVAILABLE_LOCALES;
  destinationSlug: string;
  prefetchedDestinationPage: DestinationPage;
  prefetchedDestinationPages: Array<DestinationPage>;
  prefetchedBrandIdentity: BrandIdentity;
}

const ClientSideOnlyOffersPage = ({
  locale,
  destinationPage,
  destinations,
  selectedStayRequest,
  selectedCancellationPolicyId,
  selectedLogesFilter,
}: ClientSideOnlyOffersPageProps) => {
  const [isInitializing, setIsInitializing] = useState(true);

  const router = useRouter();

  const logesFilterQuery =
    selectedLogesFilter.length > 0 && selectedLogesFilter[0].length !== 0;

  const [logesFilter] = useState(logesFilterQuery ? selectedLogesFilter : []);

  const offers = useOffers(
    destinationPage.destination.sanityId,
    selectedStayRequest,
    locale,
    selectedCancellationPolicyId,
  );

  const sortedOffers = useOffersSorter({ offers });

  const filteredOffers = sortedOffers?.offersByHotel
    .filter((oneHotelWithOffers) =>
      logesFilter.length > 0
        ? logesFilter.includes(oneHotelWithOffers.id)
        : true,
    )
    .sort((a, b) =>
      a.numberOfAvailableOffers > b.numberOfAvailableOffers ? -1 : 1,
    );

  useEffect(() => {
    if (filteredOffers?.length === 1 && offers && router && sortedOffers) {
      const hotel = filteredOffers[0];
      const hotelUrl = hotelPageRoute(
        destinationPage.seoSlug,
        hotel.slug,
        selectedStayRequest,
      );

      router.push(hotelUrl);
    } else if (offers && router && sortedOffers) setIsInitializing(false);
  }, [
    filteredOffers,
    offers,
    router,
    sortedOffers,
    destinationPage.seoSlug,
    selectedStayRequest,
  ]);

  if (!offers || !router || !sortedOffers || isInitializing)
    return <LottieOfferLoader />;
  if (offers.length === 0) return <NoOffersAvailableError />;
  if (sortedOffers.isDestinationSoldOut) return <SoldOutDestinationError />;

  return (
    <>
      <AccommodationsSection>
        <HeaderTitle>
          <Title>
            <Trans>Rooms</Trans>
          </Title>
          <Subtitle>
            <Trans>Available</Trans>
          </Subtitle>
        </HeaderTitle>

        {filteredOffers?.map((oneHotelWithOffers) => (
          <HotelCard
            key={oneHotelWithOffers.id}
            hotel={oneHotelWithOffers}
            destinationSeoSlug={destinationPage.seoSlug}
            selectedStayRequest={selectedStayRequest}
            soldOutOffers={sortedOffers?.soldOutOffers}
            locale={locale}
            destinationId={destinationPage.destination.sanityId}
          />
        ))}
      </AccommodationsSection>

      <CarouselWrapper>
        <DestinationsCarousel destinations={destinations} />
      </CarouselWrapper>

      <FaqContainer>
        <Faq />
      </FaqContainer>
    </>
  );
};

interface OffersPageParams extends ParsedUrlQuery {
  destinationSlug: string;
}

export interface OffersPageProps extends ServerSideGeneratedI18nNamespace {
  locale: AVAILABLE_LOCALES;
  destinationSlug: string;
  prefetchedDestinationPage: DestinationPage;
  prefetchedDestinationPages: Array<DestinationPage>;
  prefetchedBrandIdentity: BrandIdentity;
}

export const getStaticPaths: GetStaticPaths<OffersPageParams> = async ({
  locales = [],
}) => {
  const availableOffersPagePath = [];
  const destinationPages = await fetchDestinationPages({
    locale: DEFAULT_LOCALE,
  });

  for (const oneLocale of locales as Array<AVAILABLE_LOCALES>) {
    for (const oneDestinationPage of destinationPages) {
      // Skip destinations where guests cannot book rooms
      if (!oneDestinationPage.features.guestCanBookARoom) continue;

      const logePages = await fetchHotelPagesOfDestination({
        destinationId: oneDestinationPage.destination.sanityId,
        locale: DEFAULT_LOCALE,
      });

      // Only include destinations with available accommodations
      if (logePages.length >= 1) {
        availableOffersPagePath.push({
          params: {
            destinationSlug: oneDestinationPage.seoSlug,
          },
          locale: oneLocale,
        });
      }
    }
  }

  return {
    fallback: "blocking",
    paths: availableOffersPagePath,
  };
};

export const getStaticProps: GetStaticProps<
  OffersPageProps,
  OffersPageParams
> = async (context) => {
  const locale = (context.locale ?? context.defaultLocale) as AVAILABLE_LOCALES;
  const destinationSlug = context.params?.destinationSlug;

  if (!destinationSlug) {
    throw new Error('The "destinationSlug" param is missing in the context');
  }

  const [
    prefetchedDestinationPage,
    prefetchedDestinationPages,
    prefetchedBrandIdentity,
  ] = await Promise.all([
    fetchDestinationPage({ locale, destinationSlug }),
    fetchDestinationPages({ locale }),
    fetchBrandIdentity(),
  ]);

  if (!prefetchedDestinationPage) {
    return { notFound: true };
  }

  return {
    props: {
      locale,
      destinationSlug,
      prefetchedDestinationPage,
      prefetchedDestinationPages,
      prefetchedBrandIdentity,
      ...(await serverSideTranslations(locale)),
    },
  };
};

const OffersPage: NextPage<OffersPageProps> = ({
  locale,
  destinationSlug,
  prefetchedDestinationPage,
  prefetchedDestinationPages,
  prefetchedBrandIdentity,
}: OffersPageProps) => {
  const isSSR = useIsSSR();
  const router = useRouter();

  const { destinationPage } = useDestinationPage(
    { locale, destinationSlug },
    prefetchedDestinationPage,
  );

  const { destinationPages } = useDestinationPages(
    { locale },
    prefetchedDestinationPages,
  );

  const initialStayRequest = useStayRequest({
    firstBookableDate: destinationPage?.firstBookableDate ?? undefined,
  });

  const initialLogesFilter = useRouterQueryParameter("logeIds").split(",");

  const {
    selectedSearchableDestination,
    selectedStayRequest,
    selectedCancellationPolicyId,
  } = useSearchFromQuery({
    destinationSlug,
    stayRequest: initialStayRequest,
    cancellationPolicyId: CancellationPolicyId.FLEXIBLE,
    destinations:
      destinationPages?.map(destinationToMenuEntry) ??
      prefetchedDestinationPages.map(destinationToMenuEntry),
  });

  const selectedDestination = useMemo<DestinationMenuEntry>(() => {
    const destination = prefetchedDestinationPages.find(
      (dest) => dest.seoSlug === selectedSearchableDestination.seoSlug,
    );

    if (!destination) {
      throw new Error(
        `Could not find destination with slug: ${selectedSearchableDestination.seoSlug}`,
      );
    }

    return destinationToMenuEntry(destination);
  }, [prefetchedDestinationPages, selectedSearchableDestination.seoSlug]);

  if (!destinationPage || !destinationPages || !router)
    return <LottieOfferLoader />;

  const seoTitle = t`Our hotels Encore Mieux ${destinationPage.destination.name} | Official Site`;

  const head = (
    <NextSeo
      title={seoTitle}
      canonical={
        PUBLIC_URL + offersRoute(destinationPage.seoSlug, undefined, undefined)
      }
    />
  );

  const header = (
    <Header
      backgroundShade="lightFirst"
      shadow
      centralArea={
        router.isReady ? (
          <TextualStayRequestForm
            destination={selectedDestination}
            destinations={destinationPages.map(destinationToMenuEntry)}
            stayRequest={selectedStayRequest}
            onDestinationChange={(newDestination) => {
              router.push(
                offersRoute(newDestination.seoSlug, selectedStayRequest),
              );
            }}
            onStayRequestChange={(newStayRequest) => {
              router.push(
                offersRoute(selectedDestination.seoSlug, newStayRequest),
              );
            }}
            offerMaxCapacity={undefined}
            onNewSearch={undefined}
          />
        ) : null
      }
    >
      <Button href={gogailleJourneyRoute()} kind="secondary" size="normal">
        <Trans>My stays</Trans>
      </Button>
    </Header>
  );

  const footer = (
    <Footer
      pressKitUrl={prefetchedBrandIdentity.pressKitUrl}
      currentLocale={locale}
      availableLocales={Object.values(AVAILABLE_LOCALES)}
      onLocaleChange={(newLocale) => {
        router
          .push(
            offersRoute(selectedDestination.seoSlug, selectedStayRequest),
            undefined,
            { locale: newLocale },
          )
          .then(() => window.scrollTo(0, 0));
      }}
    />
  );

  // SSR loading state
  if (isSSR) {
    return (
      <OfferSearchLayout head={head} header={header} footer={footer}>
        <LottieOfferLoader />
      </OfferSearchLayout>
    );
  }

  return (
    <OfferSearchLayout head={head} header={header} footer={footer}>
      <ErrorBoundary
        key={offersRoute(selectedDestination.seoSlug, selectedStayRequest)}
        renderOnPastStay={PastStayError}
        renderOnOutOfBoundsNumberOfGuests={OutOfBoundsGuestsError}
      >
        {router.isReady ? (
          <ClientSideOnlyOffersPage
            locale={locale}
            destinationPage={destinationPage}
            destinations={destinationPages}
            selectedDestination={selectedDestination}
            selectedStayRequest={selectedStayRequest}
            selectedCancellationPolicyId={selectedCancellationPolicyId}
            selectedLogesFilter={initialLogesFilter}
          />
        ) : (
          <LottieOfferLoader />
        )}
      </ErrorBoundary>
    </OfferSearchLayout>
  );
};

export default OffersPage;
